import type { GenerateAppSchemaParameters, Manifest, v4, ExtensionLogger } from '../../../..';
import { DefinitionName, log, LogSeverity, PropertyName, SchemaTag, TemplateType, Visualization } from '../../../..';
import type { LineItemDataForSchema } from '../../utils';
import {
    addCustomColumnDefinition,
    addFragmentEnumForAnchor,
    addLineItemsTypeToSchema,
    alignSchemaWithTemplateType
} from '../../utils';
import type { ListReportPageConfig, SchemaAdjustmentParameters, ViewsParameters } from '../list-report/listReport';
import {
    addALPViews,
    addLRViewParts,
    addManifestPathsToPropertiesLR,
    addSelectionFields,
    addTagsToView,
    addVisualFilters,
    analyzeViewAnnotation,
    applyCustomFilterFields,
    applyLRSchemaAdjustments,
    getVisualFilters
} from '../list-report/listReport';
import type { Page, App, AppSchema } from '../../sync-rules';
import type { LineItem } from '@sap-ux/vocabularies-types/vocabularies/UI';
import { UIAnnotationTerms } from '@sap-ux/vocabularies-types/vocabularies/UI';
import {
    createAnnotationPath,
    type DecoratorContext,
    getCustomExtensionDefinitionName,
    getDefinitionKey,
    prepareRef
} from '../../../common';
import i18next from 'i18next';
import type { AnnotationTerm, EntitySet, EntityType } from '@sap-ux/vocabularies-types';
import type { Definition } from 'typescript-json-schema';

/**
 * Add general settings to schema (LR or ALP).
 *
 * - Adds Selection-(PresentationVariant) related enums to the schema.
 */
export class GeneralListReportSettings {
    private readonly schema: AppSchema;
    private readonly page: Page | undefined;

    /**
     * Constructor for general settings of ListReport.
     *
     * @param context - general settings (App, Page, AppSchema, Logger)
     */
    constructor(context: DecoratorContext) {
        this.schema = context.schema;
        this.page = context.page;
    }

    init() {
        this.addSPVEnums(DefinitionName.AnnotationPathAsObject);
    }

    /**
     * Adds Selection-(PresentationVariant) related enums to the schema.
     *
     * @param definitionName - definition name
     */
    private addSPVEnums(definitionName: DefinitionName) {
        const targetTerms = [UIAnnotationTerms.SelectionVariant, UIAnnotationTerms.SelectionPresentationVariant];
        const uiAnnotations = this.page?.getUIAnnotations();
        if (!uiAnnotations) {
            return;
        }
        const enumStrings = Object.values(uiAnnotations)
            .filter((annotation) => targetTerms.includes(annotation.term as UIAnnotationTerms))
            .map((annotation) =>
                annotation.qualifier ? `${annotation.term}#${annotation.qualifier}` : annotation.term
            );
        const enums = [...new Set(enumStrings)];
        this.schema.addEnumToSchema(definitionName, ['properties', SchemaTag.annotationPath], enums);
    }
}

/**
 * Add filter bar to schema (LR or ALP).
 */
export class FilterBar {
    private readonly schema: AppSchema;
    private readonly app: App | undefined;
    private readonly page: Page | undefined;

    /**
     * @param context - general settings (App, Page, AppSchema, Logger)
     */
    constructor(context: DecoratorContext) {
        this.schema = context.schema;
        this.app = context.app;
        this.page = context.page;
    }

    /**
     * Initializes the filter bar adjustments.
     */
    init() {
        this.addFilterBar(
            this.page?.getEntityType(),
            this.schema,
            this.page?.target,
            this.page?.getUIVocabularyAlias()
        );
    }

    // Implementation for FilterBar can be added here

    private addVisualFilters() {
        getVisualFilters(this.page?.target);
    }
    private addSelectionFields() {}

    /**
     * Adds filter bar to schema (LR or ALP).
     *
     * @param entityType - the entity type
     * @param schema - the application schema
     * @param v4Page - the actual page in the manifest
     * @param alias - alias definition for the UI vocabulary
     */
    private addFilterBar(
        entityType: EntityType,
        schema: AppSchema,
        v4Page: v4.SapUiAppPageV4 | undefined,
        alias: string | undefined
    ) {
        const selectionFields = alias && entityType?.annotations?.[alias]?.SelectionFields;
        let selectionFieldsDefinition;
        // Hide visualFilters/selectionFields depending on existing visual filters
        const visualFilters = getVisualFilters(v4Page);
        if (visualFilters) {
            schema.setProperty(DefinitionName.SelectionFields, [SchemaTag.hidden], true);
            schema.setProperty(DefinitionName.SelectionFields, ['properties'], {});
            schema.setProperty(DefinitionName.CompactFilters, ['properties'], {});
            schema.setProperty(DefinitionName.CompactFilters, ['additionalProperties'], false);
            selectionFieldsDefinition = schema.getDefinition(DefinitionName.CompactFilters);
            addVisualFilters(entityType, schema.get(), visualFilters, selectionFields);
        } else {
            schema.setProperty(DefinitionName.FilterBar, ['properties', 'initialLayout', SchemaTag.hidden], true);
            schema.setProperty(DefinitionName.FilterBar, ['properties', 'layout', SchemaTag.hidden], true);
            schema.setProperty(DefinitionName.CompactFilters, [SchemaTag.hidden], true);
            schema.setProperty(DefinitionName.VisualFilters, [SchemaTag.hidden], true);
        }
        selectionFieldsDefinition = addSelectionFields(
            entityType,
            schema.get(),
            selectionFields,
            selectionFieldsDefinition
        );
        // Apply custom filter fields
        applyCustomFilterFields(schema.get(), selectionFieldsDefinition, v4Page);
        // Add "manifestPath" to filter fields
        addManifestPathsToDefinitionPropertiesLR(
            schema.get(),
            v4Page?.id,
            DefinitionName.SelectionFields,
            this.app?.getManifest()
        );
    }
}

/**
 * Adjusts the entity type if a different entity set is maintained for a view.
 *
 * @param {EntitySet} entitySet - actual entity set (AVT information)
 * @param view - the current view in manifest
 * @param page - the page object
 * @param app - the app object
 * @returns the target entity type
 */
function getTargetEntityType(entitySet: EntitySet, view: object, page: Page, app: App): EntityType {
    let targetEntityType = page.getEntityType();
    let viewEntitySet = entitySet;
    if (view['entitySet'] && view['entitySet'] !== entitySet.name) {
        viewEntitySet = app.getAVT()?.entitySets.find((es) => {
            return es.name === view['entitySet'];
        });
        targetEntityType = viewEntitySet.entityType;
    }
    return targetEntityType;
}

/**
 * Adapts the table definition in schema.
 * Evaluates the path depending on if defaultTemplateAnnotationPath or Selection-/PresentationVariant is present.
 *
 * @param context - general settings (App, Page, AppSchema, Logger)
 * @param templateAnnotation - defaultTemplateAnnotationPath as registered in manifest
 * @param tableDefinitionName - The name of the table definition in the schema
 */
function adaptTableDefinition(
    context: DecoratorContext,
    templateAnnotation: string,
    tableDefinitionName: DefinitionName.Table | DefinitionName.ALPTableView
) {
    const { schema, page, app } = context;
    let schemaIdForColumns = `${DefinitionName.LineItems}`;
    let schemaIdForActions;

    //here: tableDefinitionName = DefinitionName.Table
    const tableDefinition = schema.getDefinition(tableDefinitionName);
    const { visualization, targetAnnotation, namespace } = analyzeViewAnnotation(
        templateAnnotation,
        page.getUIAnnotations(),
        app.getAVT(),
        undefined,
        Visualization.LineItem,
        app.logger
    );
    if (visualization) {
        const annotationTerm = visualization.split('.')[1];
        schemaIdForColumns = `${DefinitionName.LineItems}OfSPV::${prepareRef(annotationTerm)}`;
        const spvTable = schema.cloneDefinition(tableDefinitionName, `${tableDefinitionName}SPV`);

        schema.setPropertyRef(spvTable, ['properties', 'toolBar'], `${DefinitionName.ToolBar}<${schemaIdForColumns}>`);

        // Switch from generic column to specific definition
        // schema.setProperty(spvTable, ['properties', 'columns'], {});
        schema.setProperty(spvTable, [SchemaTag.annotationPath], targetAnnotation);

        schema.setPropertyRef(spvTable, ['properties', 'columns'], schemaIdForColumns);
        schema.setDefinitionRef(tableDefinition, `${tableDefinitionName}SPV`);

        schema.removeProperty(spvTable, SchemaTag.isViewNode);
        schema.removeProperty(tableDefinition, 'properties');
        schema.setProperty(tableDefinition, [SchemaTag.annotationPath], `/${namespace}/@${templateAnnotation}`);
        const lineItemAnnotation = page.getUIAnnotations()[annotationTerm] as AnnotationTerm<LineItem>;
        if (!lineItemAnnotation) {
            log(app.logger, {
                severity: LogSeverity.Error,
                message: i18next.t('NOTARGET', { target: annotationTerm })
            });
        }
        const dataForLineItem: LineItemDataForSchema = {
            isObjectPage: false,
            appSchema: schema.get(),
            lineItemAnnotation,
            entityType: page.entity.type,
            oDataServiceAVT: app.getAVT(),
            lineItemId: schemaIdForColumns
        };
        addLineItemsTypeToSchema(dataForLineItem);
        const annotationTermQualifier = annotationTerm.split('#')?.[1];
        const sectionIdInManifest = annotationTermQualifier
            ? `@${UIAnnotationTerms.LineItem}#${annotationTermQualifier}`
            : `@${UIAnnotationTerms.LineItem}`;
        addCustomColumnDefinition(
            schema.get(),
            page.target,
            app.logger,
            undefined,
            schemaIdForColumns,
            sectionIdInManifest
        );
        addFragmentEnumForAnchor(schema.get(), schemaIdForColumns, page.target, undefined, sectionIdInManifest);

        // Table toolbar actions with custom actions
        schemaIdForActions = `${DefinitionName.Actions}<${schemaIdForColumns}>`;
        const customColumnDefinitionName = getCustomExtensionDefinitionName(
            schema.get(),
            schemaIdForColumns,
            DefinitionName.CustomTableAction
        );

        const schemaAdjustmentParameters: SchemaAdjustmentParameters = {
            logger: app.logger,
            appSchema: schema.get(),
            v4Page: page.target,
            tableDefinitionKey: `${tableDefinitionName}SPV`,
            facetDefinitionKey: schemaIdForColumns,
            dataForLineItem,
            entityType: page.entity.type,
            entitySet: page.entity.set,
            sectionActionsDefinitionKey: schemaIdForActions,
            scopedCustomActionDefinitionKey: getDefinitionKey(customColumnDefinitionName),
            customActionDefinitionName: DefinitionName.CustomTableAction,
            sectionIdInManifest
        };
        applyLRSchemaAdjustments(schemaAdjustmentParameters);
    } else {
        schema.setPropertyRef(tableDefinition, ['properties', 'toolBar'], `${DefinitionName.ToolBarLR}`);
        schema.setPropertyRef(tableDefinition, ['properties', 'columns'], `${DefinitionName.LineItems}`);
        // Switch from generic column to specific definition
        const lineItemAnnotation = page.getUIAnnotations()?.LineItem;
        if (!lineItemAnnotation && !page.target.options?.settings?.views?.paths) {
            log(app.logger, {
                severity: LogSeverity.Error,
                message: i18next.t('NOLINEITEMS')
            });
        }
        const dataForLineItem: LineItemDataForSchema = {
            isObjectPage: false,
            appSchema: schema.get(),
            lineItemAnnotation,
            entityType: page.entity.type,
            oDataServiceAVT: app.getAVT(),
            lineItemId: DefinitionName.LineItems
        };
        addLineItemsTypeToSchema(dataForLineItem);
        addCustomColumnDefinition(schema.get(), page.target, app.logger);
        addFragmentEnumForAnchor(schema.get(), DefinitionName.LineItems, page.target);

        schemaIdForActions = `${DefinitionName.Actions}<${prepareRef(schemaIdForColumns)}>`;

        const schemaAdjustmentParameters: SchemaAdjustmentParameters = {
            logger: app.logger,
            appSchema: schema.get(),
            v4Page: page.target,
            tableDefinitionKey: tableDefinitionName,
            facetDefinitionKey: schemaIdForColumns,
            dataForLineItem,
            entityType: page.entity.type,
            entitySet: page.entity.set,
            customActionDefinitionName: DefinitionName.CustomTableAction,
            sectionActionsDefinitionKey: schemaIdForActions
        };
        applyLRSchemaAdjustments(schemaAdjustmentParameters);

        if (lineItemAnnotation && !page.target.options?.settings?.views?.paths) {
            tableDefinition[SchemaTag.annotationPath] = createAnnotationPath(
                page.entity.type.fullyQualifiedName,
                lineItemAnnotation.term,
                lineItemAnnotation.qualifier
            );
        }
    }
    // Add "manifestPath" for table properties
    addManifestPathsToPropertiesLR(schema.get(), tableDefinitionName, tableDefinition, {
        pageName: page.target.id,
        manifest: app.getManifest(),
        targetAnnotation
    });
    // Add "manifestPath" for table columns and actions
    const customExtensionDefinitionNames = [schemaIdForColumns, schemaIdForActions];
    customExtensionDefinitionNames.forEach((definitionName) => {
        addManifestPathsToDefinitionPropertiesLR(schema.get(), page.target.id, definitionName, app.getManifest());
    });
}

/**
 * Add list report views to the app-specific schema.
 *
 * @param context - general settings (App, Page, AppSchema, Logger)
 * @param tableDefinitionName - The name of the table definition in the schema
 */
function addLRViews(
    context: DecoratorContext,
    tableDefinitionName: DefinitionName.Table | DefinitionName.ALPTableView
) {
    const { schema, app, page } = context;
    const viewsPaths = (page.config as ListReportPageConfig)?.views?.paths;
    const tableDefinition = schema.getDefinition(tableDefinitionName);
    tableDefinition.properties = {
        annotationPath: tableDefinition.properties.annotationPath,
        initialLoad: tableDefinition.properties.initialLoad,
        views: tableDefinition.properties.views
    };
    const viewsDefinition = schema.getDefinition(DefinitionName.MultiTableModeV4);
    for (const view of viewsPaths) {
        if (!view[SchemaTag.key]) {
            continue;
        }
        let navPropName: string = undefined;
        const targetEntityType = getTargetEntityType(page.entity.set, view, page, app);
        const uiAnnotations = targetEntityType?.annotations.UI;
        const { visualization, targetAnnotation, namespace, title } = analyzeViewAnnotation(
            view?.[SchemaTag.annotationPath],
            uiAnnotations,
            app.getAVT(),
            view[SchemaTag.key],
            undefined,
            app.logger
        );
        if (view['entitySet']) {
            navPropName = page.entity.type.navigationProperties.find((np) => {
                return np.targetTypeName === namespace;
            })?.name;
        }
        const viewKey = prepareRef(view[SchemaTag.key]);
        const commonInputParameters: ViewsParameters = {
            viewKey,
            appSchema: schema.get(),
            targetAnnotation,
            navPropName,
            targetEntityType,
            title,
            entitySet: view['entitySet']
        };
        addLRViewParts(
            visualization,
            viewsDefinition,
            commonInputParameters,
            { serviceAVT: app.getAVT(), entityType: targetEntityType, logger: app.logger },
            page,
            app,
            uiAnnotations,
            view
        );
        addTagsToView(viewsDefinition, viewKey, namespace, view);
    }
}

export class TableAndViews {
    private readonly schema: AppSchema;
    private readonly app: App;
    private readonly page: Page;
    private readonly logger?: ExtensionLogger;

    /**
     * Creates a TableAndViews instance with the given settings.
     *
     * @param context - General settings containing schema, app, page, and optional logger
     */
    constructor(context: DecoratorContext) {
        this.schema = context.schema;
        this.app = context.app;
        this.page = context.page;
        this.logger = context.logger;
    }

    /**
     * Gets the general settings.
     *
     * @returns The general settings object
     */
    getContext(): DecoratorContext {
        return {
            schema: this.schema,
            app: this.app,
            page: this.page,
            logger: this.logger
        };
    }
    /**
     * Initializes the addition of table and views to the schema.
     */
    init() {
        this.addTableAndViews();
    }

    /**
     * Adds table and views to the schema.
     */
    private addTableAndViews() {
        const tableDefinitionName = alignSchemaWithTemplateType(this.schema.get(), this.page.template.type);
        const config = this.page.config as ListReportPageConfig;

        if (this.page.template.type === TemplateType.ListReportObjectPageV4) {
            this.schema.hide(this.schema.getRootProperty([PropertyName.chart]));

            adaptTableDefinition(this.getContext(), config?.defaultAnnotation, tableDefinitionName);

            if (!config?.views) {
                this.schema.setProperty(DefinitionName.MultiTableModeV4, ['properties'], {});
            } else if (config.views.paths) {
                this.schema.cleanup(['LineItems', 'ToolBar<LineItems>', 'Actions<LineItems>']);
                addLRViews(this.getContext(), tableDefinitionName);
            }
        } else if (config?.isALP) {
            const generateParameters = {
                logger: this.logger,
                entityType: this.page.getEntityType(),
                serviceAVT: this.app.getAVT()
            } as GenerateAppSchemaParameters;

            addALPViews(
                this.schema.get(),
                config.views?.paths,
                generateParameters,
                config.defaultAnnotation,
                this.page.target,
                tableDefinitionName
            );
            addFragmentEnumForAnchor(this.schema.get(), DefinitionName.LineItems, this.page.target);
        }
    }
}

/**
 * Enhances the List Report schema with manifest paths for definition and properties.
 *
 * This function extends the app schema (`schema`) by adding manifestPath to definition and properties on the List Report.
 *
 * @param {Definition} schema - The application schema object representing the List Report structure.
 * @param {string} pageId - Identifier of the page.
 * @param {string} definitionName - Identifier of the corresponding definition.
 * @param {Manifest} manifest - The application manifest.
 * @param {string} targetAnnotation - Target annotation.
 * @param {string} entitySet - Entity set.
 */
export function addManifestPathsToDefinitionPropertiesLR(
    schema: Definition,
    pageId: string,
    definitionName: string,
    manifest: Manifest,
    targetAnnotation?: string,
    entitySet?: string
): void {
    const matchingDefinition = schema.definitions?.[definitionName] as Definition;
    for (const key in matchingDefinition?.properties ?? {}) {
        const property = matchingDefinition.properties?.[key] as Definition;
        addManifestPathsToPropertiesLR(
            schema,
            undefined,
            property,
            {
                pageName: pageId,
                columnKey: key,
                manifest,
                targetAnnotation,
                viewConfig: {
                    entitySet
                }
            },
            true
        );
    }
}
